import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, X, ExternalLink } from "lucide-react";

// One block that manages an ORDERED LIST of scheduled announcements — a
// full campaign timeline (volunteer call → vendor call → parade lineup →
// day-of weather alert). Renderer auto-picks whichever entries are
// currently within their [start_at, end_at] window. If none are in-window,
// the block renders nothing on the public page.
//
// Each entry:
//   { id, message, cta_label, cta_route, accent, start_at, end_at,
//     dismissable, priority: "info" | "warning" | "urgent" }
//
// start_at / end_at are local ISO strings (YYYY-MM-DDTHH:MM). Empty means
// "no bound" — an entry with no start shows immediately; no end shows
// forever. Dismiss state is stored in localStorage per-entry-id so the
// admin can push a new message without clobbering the last dismissal.

const PRIORITY_STYLES = {
  info:    { bg: "#1B3A5C", fg: "#FFFFFF", ring: "#F2CC8F" },
  warning: { bg: "#E76F51", fg: "#FFFFFF", ring: "#FFE8DE" },
  urgent:  { bg: "#C0392B", fg: "#FFFFFF", ring: "#FFB4B4" },
};

const inWindow = (entry, now) => {
  const start = entry.start_at ? new Date(entry.start_at).getTime() : null;
  const end = entry.end_at ? new Date(entry.end_at).getTime() : null;
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
};

const AnnouncementTickerBlock = ({ block }) => {
  const cfg = block.config || {};
  const now = Date.now();
  const entries = (cfg.announcements || []).filter((e) => e && e.message);
  const active = useMemo(() => entries.filter((e) => inWindow(e, now)), [entries, now]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("wall_ann_dismissed") || "[]")); }
    catch { return new Set(); }
  });
  const shown = active.filter((e) => !e.dismissable || !dismissed.has(e.id));
  if (shown.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    try { localStorage.setItem("wall_ann_dismissed", JSON.stringify(Array.from(next))); } catch { /* ignore */ }
  };

  return (
    <div className="w-full" data-testid={`fest-announcements-${block.block_id}`}>
      {shown.map((e) => {
        const style = PRIORITY_STYLES[e.priority] || PRIORITY_STYLES.info;
        const bg = e.accent || style.bg;
        const isExternal = e.cta_route && /^https?:\/\//i.test(e.cta_route);
        const cta = e.cta_label && e.cta_route ? (
          isExternal ? (
            <a href={e.cta_route} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline underline-offset-2 font-semibold whitespace-nowrap">
              {e.cta_label} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <Link to={e.cta_route}
              className="inline-flex items-center gap-1 underline underline-offset-2 font-semibold whitespace-nowrap">
              {e.cta_label} →
            </Link>
          )
        ) : null;
        return (
          <div
            key={e.id}
            style={{ backgroundColor: bg, color: style.fg }}
            className="w-full py-3 px-6 md:px-12 border-b-2"
            data-testid={`announcement-${e.id}`}
          >
            <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
              <Megaphone className="h-5 w-5 shrink-0" />
              <p className="flex-1 min-w-0 text-sm md:text-base font-medium m-0 leading-snug">
                {e.message}
              </p>
              {cta}
              {e.dismissable && (
                <button
                  onClick={() => dismiss(e.id)}
                  aria-label="Dismiss"
                  className="rounded-full hover:bg-white/20 p-1 shrink-0"
                  data-testid={`announcement-dismiss-${e.id}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnnouncementTickerBlock;
